import { useState } from 'react';
import { useT } from '../../i18n/i18n';
import { labelWithTag } from '../../i18n/labels';
import { pickCoverFileAction, type CoverLane, type PendingCover } from '../../state/coverPhoto';
import { ActionSheet, Button, Icon, showToast } from '../../ui/components';

// Report cover photo on the New Inspection form (Owner 2026-09-14). The inspection does not exist yet,
// so the picked photo is held by the form (PendingCover) and stored the moment Create succeeds. Camera
// captures already had their album save requested when they were picked (src/state/coverPhoto.ts).
//
// Owner-selected design (Figma 191:7 "NEW · B"): the field block in step 1 carries the note and the two
// capture actions; the picked photo shows in the summary card at the top of every step (CoverSummary)
// with 更换封面 · 移除封面 under the address.
function useCoverPicker(onChange: (next: PendingCover | null) => void) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  async function pick(lane: CoverLane) {
    setBusy(true);
    try {
      const outcome = await pickCoverFileAction(lane);
      if (outcome.kind === 'picked') onChange(outcome.pending);
      else if (outcome.kind === 'error') showToast(t('cover.saveFailed', { message: outcome.message }), 'info');
    } finally {
      setBusy(false);
    }
  }
  return { busy, pick };
}

export function CoverPhotoField(props: { value: PendingCover | null; onChange: (next: PendingCover | null) => void; testId?: string }) {
  const { t, locale } = useT();
  const { busy, pick } = useCoverPicker(props.onChange);
  return (
    <div className="cover-field" data-testid={props.testId ?? 'cover-field'} data-has-cover={props.value ? 'true' : 'false'}>
      <p className="form-block-label">{labelWithTag(locale, t('cover.fieldTitle'), t('common.optional'))}</p>
      <p className="card-note">{t('cover.fieldBody')}</p>
      <div className="btn-pair cover-field-actions">
        <Button variant="secondary" size="regular" icon="photo_camera" onClick={() => void pick('camera')} disabled={busy} testId="cover-field-camera">
          {t('photo.takePhoto')}
        </Button>
        <Button variant="secondary" size="regular" icon="photo_library" onClick={() => void pick('library')} disabled={busy} testId="cover-field-library">
          {t('photo.chooseLibrary')}
        </Button>
      </div>
      {props.value ? <p className="card-note">{t('cover.pendingNote')}</p> : null}
    </div>
  );
}

// The summary card's cover slot: the picked photo with 更换封面 · 移除封面, or a tappable placeholder tile
// that offers the same two capture actions.
export function CoverSummary(props: { value: PendingCover | null; onChange: (next: PendingCover | null) => void }) {
  const { t } = useT();
  const { busy, pick } = useCoverPicker(props.onChange);
  const [sheet, setSheet] = useState(false);
  const actions = [
    { key: 'camera', label: t('photo.takePhoto'), icon: 'photo_camera' as const, testId: 'cover-field-camera-sheet' },
    { key: 'library', label: t('photo.chooseLibrary'), icon: 'photo_library' as const, testId: 'cover-field-library-sheet' }
  ];
  return (
    <>
      {props.value ? (
        <div className="cover-summary cover-summary-has" data-testid="cover-field-preview">
          {props.value.previewUrl ? (
            <img src={props.value.previewUrl} alt="" />
          ) : (
            <span className="photo-card-empty">
              <Icon name="image" size={28} />
            </span>
          )}
        </div>
      ) : (
        <button type="button" className="cover-summary cover-summary-empty" onClick={() => setSheet(true)} disabled={busy} data-testid="cover-summary-add">
          <Icon name="add_a_photo" />
          <span>{t('setup.coverSummary')}</span>
        </button>
      )}
      {props.value ? (
        <p className="cover-summary-links">
          <button type="button" className="link-inline" onClick={() => setSheet(true)} disabled={busy} data-testid="cover-field-replace">
            {t('cover.replace')}
          </button>
          <span aria-hidden="true"> · </span>
          <button type="button" className="link-inline" onClick={() => props.onChange(null)} disabled={busy} data-testid="cover-field-remove">
            {t('cover.remove')}
          </button>
        </p>
      ) : null}
      {sheet ? (
        <ActionSheet
          title={props.value ? t('cover.replace') : t('cover.fieldTitle')}
          items={actions.map((a) => ({
            key: a.key,
            label: a.label,
            icon: a.icon,
            onSelect: () => {
              setSheet(false);
              void pick(a.key as CoverLane);
            },
            testId: a.testId
          }))}
          onClose={() => setSheet(false)}
          testId="cover-field-sheet"
        />
      ) : null}
    </>
  );
}
